import React from "react";
import { ComposedChart, Tooltip } from "recharts";
import styled from "@emotion/styled";
import { Box, Theme, useMediaQuery, useTheme } from "@mui/material";
import AutoSizer from "react-virtualized-auto-sizer";
import { useRangeSelectControl, useChartColors } from "@hooks";
import { RangeSelector } from "../../common";
import { GridAndAxis, MainLine, useTickFormat } from "../BaseChartTools";
import { SecuritiesDateAndPriceChartTooltip } from "./SecuritiesDateAndPriceChartTooltip";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

interface ActivePayloadItem {
  dataKey: "value";
  name: string;
  value: number;
  color: string;
  payload: {
    date: Date;
    value: number;
    areaValue: [number, number];
  };
}

export interface SecuritiesDateAndPriceChartDataItem {
  date: Date;
  value: number;
  areaValue: [number, number];
}

export interface SecuritiesDateAndPriceChartProps {
  data: SecuritiesDateAndPriceChartDataItem[];
  onDateSelect: (type: IntervalType, value: RangeType) => void;
  onValuesSelect: (value?: number, date?: Date) => void;
}

export const SecuritiesDateAndPriceChart = (
  props: SecuritiesDateAndPriceChartProps
) => {
  const { data = [], onDateSelect, onValuesSelect } = props;
  const chartColors = useChartColors("securitiesChart");

  const theme = useTheme();
  const { range, intervalType, customIntervalSelect, selectInterval } =
    useRangeSelectControl({
      onSelect: onDateSelect,
      firstDate: data[0] ? data[0].date : new Date(),
    });
  const isXs = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const tickFormat = useTickFormat(range.startDate, range.endDate);

  const tickInterval = Math.floor(data.length / (isXs ? 6 : 12));

  const intervalTypes: IntervalType[] = [
    "oneMonth",
    "oneYear",
    "threeYears",
    "fiveYears",
    "sinceBeginning",
  ];

  const handleChartClick = (payload: any) => {
    const valuePayload = payload?.activePayload?.find(
      (item: ActivePayloadItem) => item.dataKey === "value"
    );

    onValuesSelect(valuePayload?.payload?.value, valuePayload?.payload?.date);
  };

  return (
    <Wrapper>
      <Box height="280px">
        <AutoSizer>
          {({ width }) => (
            <ComposedChart
              data={data}
              onClick={handleChartClick}
              width={width}
              height={280}
            >
              {GridAndAxis({
                theme,
                tickFormat,
                tickInterval,
                xDataKey: "date",
              })}
              <Tooltip content={<SecuritiesDateAndPriceChartTooltip />} />
              {MainLine({
                dataKey: "value",
                dataAreaKey: "areaValue",
                chartColors,
              })}
            </ComposedChart>
          )}
        </AutoSizer>
      </Box>

      <RangeSelector
        intervalType={intervalType}
        intervalTypes={intervalTypes}
        onSelect={selectInterval}
        onCustomSelect={customIntervalSelect}
      />
    </Wrapper>
  );
};
